import { useState, useRef, useEffect } from "react";
import { useNavigate } from "react-router-dom";
import { setupRecaptcha, sendPhoneVerification, verifyPhoneCode } from "../services/authService";
import { ICON_LG } from "../constants/icons";
import { useAuth } from "../contexts/AuthContext";

export default function Login() {
  const { currentUser, loading: authLoading } = useAuth();
  const [step, setStep] = useState("phone");
  const [phone, setPhone] = useState("");
  const [code, setCode] = useState("");
  const [error, setError] = useState("");
  const [loading, setLoading] = useState(false);
  const [confirmResult, setConfirmResult] = useState(null);
  const [nativeVerificationId, setNativeVerificationId] = useState(null);
  const recaptchaRef = useRef(null);
  const navigate = useNavigate();

  

  useEffect(() => {
    if (!authLoading && currentUser) {
      navigate("/chat", { replace: true });
    }
  }, [authLoading, currentUser, navigate]);
const formatPhone = (p) => {
    let formatted = p.replace(/[-\s]/g, "");
    if (formatted.startsWith("0")) formatted = "+81" + formatted.slice(1);
    if (!formatted.startsWith("+")) formatted = "+81" + formatted;
    return formatted;
  };

  const handleSendCode = async () => {
    setError("");
    if (!phone.trim()) { setError("電話番号を入力してください"); return; }
    setLoading(true);

    try {
      const formatted = formatPhone(phone);
      const { Capacitor } = await import("@capacitor/core");

      if (Capacitor.isNativePlatform()) {
        const { FirebaseAuthentication } = await import("@capacitor-firebase/authentication");
        // Listen for verificationId via event
        await new Promise((resolve, reject) => {
          const timeout = setTimeout(() => reject(new Error("SMS送信がタイムアウトしました")), 30000);
          FirebaseAuthentication.addListener("phoneCodeSent", (event) => {
            clearTimeout(timeout);
            setNativeVerificationId(event.verificationId);
            setStep("code");
            resolve();
          });
          FirebaseAuthentication.addListener("phoneVerificationFailed", (event) => {
            clearTimeout(timeout);
            reject(new Error(event.message || "SMS送信に失敗しました"));
          });
          FirebaseAuthentication.signInWithPhoneNumber({ phoneNumber: formatted });
        });
      } else {
        if (!recaptchaRef.current) {
          recaptchaRef.current = setupRecaptcha("recaptcha-container");
        }
        const result = await sendPhoneVerification(formatted, recaptchaRef.current);
        setConfirmResult(result);
        setStep("code");
      }
    } catch (e) {
      console.error(e);
      const errMsg = e?.message || e?.code || JSON.stringify(e) || "不明なエラー";
      console.error("SMS Error details:", errMsg, e);
      setError("SMSの送信に失敗しました: " + errMsg);
      recaptchaRef.current = null;
    }
    setLoading(false);
  };

  const handleVerifyCode = async () => {
    setError("");
    if (!code.trim()) { setError("認証コードを入力してください"); return; }
    setLoading(true);

    try {
      const { Capacitor } = await import("@capacitor/core");

      if (Capacitor.isNativePlatform() && nativeVerificationId) {
        const { FirebaseAuthentication } = await import("@capacitor-firebase/authentication");
        await FirebaseAuthentication.confirmVerificationCode({
          verificationId: nativeVerificationId,
          verificationCode: code,
        });
        // Get ID token from native auth and use custom token to sign in web SDK
        const idTokenResult = await FirebaseAuthentication.getIdToken({ forceRefresh: true });
        if (idTokenResult?.token) {
          const { getFunctions, httpsCallable } = await import("firebase/functions");
          const { signInWithCustomToken } = await import("firebase/auth");
          const { auth } = await import("../config/firebase");
          const functions = getFunctions(undefined, "us-central1");
          
          // Call cloud function with native ID token to get custom token
          const response = await fetch("https://us-central1-dresswork-44979.cloudfunctions.net/createCustomTokenFromIdToken", {
            method: "POST",
            headers: { 
              "Content-Type": "application/json",
              "Authorization": "Bearer " + idTokenResult.token,
            },
            body: JSON.stringify({ uid: (await FirebaseAuthentication.getCurrentUser()).user.uid }),
          });
          const data = await response.json();
          if (data.token) {
            await signInWithCustomToken(auth, data.token);
          }
        }
      } else {
        await verifyPhoneCode(confirmResult, code);
      }
      navigate("/chat");
    } catch (e) {
      console.error(e);
      setError("認証コードが正しくありません。");
    }
    setLoading(false);
  };

  return (
    <div className="dw-login">
      <div className="dw-login__bg1" />
      <div className="dw-login__bg2" />

      <div className="dw-login__content">
        <div className="dw-login__icon">
          <img src={ICON_LG} alt="DressWork" />
        </div>
        <h1 className="dw-login__title">DressWork</h1>
        <p className="dw-login__subtitle">あなたにぴったりのお店が見つかる</p>

        {step === "phone" && (
          <div className="dw-login__form">
            <label className="dw-login__label">電話番号</label>
            <input
              type="tel"
              value={phone}
              onChange={e => setPhone(e.target.value)}
              placeholder="090-1234-5678"
              className="dw-login__input"
            />
            <p className="dw-login__hint">SMSで認証コードをお送りします</p>
            {error && <p className="dw-login__error">{error}</p>}
            <button
              onClick={handleSendCode}
              disabled={loading}
              className={`dw-login__btn ${loading ? "dw-login__btn--loading" : ""}`}
            >
              {loading ? "送信中..." : "認証コードを送信"}
            </button>
          </div>
        )}

        {step === "code" && (
          <div className="dw-login__form">
            <label className="dw-login__label">認証コード</label>
            <input
              type="text"
              inputMode="numeric"
              value={code}
              onChange={e => setCode(e.target.value)}
              placeholder="123456"
              className="dw-login__input dw-login__input--code"
              maxLength={6}
            />
            <p className="dw-login__hint">{phone} に送信されたコードを入力</p>
            {error && <p className="dw-login__error">{error}</p>}
            <button
              onClick={handleVerifyCode}
              disabled={loading}
              className={`dw-login__btn ${loading ? "dw-login__btn--loading" : ""}`}
            >
              {loading ? "確認中..." : "ログイン"}
            </button>
            <button onClick={() => { setStep("phone"); setCode(""); setError(""); }} className="dw-login__back">
              電話番号を変更する
            </button>
          </div>
        )}

        <div id="recaptcha-container" />
      </div>

      <div className="dw-login__footer">© DressWork</div>

      <style>{`
:root{--gold:#d4af6e;--gold-dk:#c49a50;--gold-lt:#f0d48a;--dark:#1a1416;--dark-m:#2a2024;--ivory:#f5efe6;--fb:'Zen Kaku Gothic New','Noto Sans JP',-apple-system,sans-serif;--fd:'Cormorant Garamond','Noto Serif JP',serif}

.dw-login{min-height:100vh;min-height:100dvh;display:flex;flex-direction:column;align-items:center;justify-content:center;background:linear-gradient(170deg,var(--dark) 0%,var(--dark-m) 50%,#1e181c 100%);font-family:var(--fb);position:relative;overflow:hidden}
.dw-login__bg1{position:absolute;top:-60px;right:-40px;width:200px;height:200px;border-radius:50%;background:radial-gradient(circle,rgba(212,175,110,.06) 0%,transparent 70%);pointer-events:none}
.dw-login__bg2{position:absolute;bottom:80px;left:-50px;width:180px;height:180px;border-radius:50%;background:radial-gradient(circle,rgba(168,85,160,.03) 0%,transparent 70%);pointer-events:none}

.dw-login__content{position:relative;z-index:2;width:100%;max-width:380px;padding:40px 28px;text-align:center}
.dw-login__icon{width:80px;height:80px;border-radius:22px;overflow:hidden;margin:0 auto 20px;box-shadow:0 8px 32px rgba(212,175,110,.25)}
.dw-login__icon img{width:100%;height:100%;object-fit:cover}
.dw-login__title{font-size:28px;font-weight:300;color:var(--ivory);letter-spacing:.12em;font-family:var(--fd);margin-bottom:6px}
.dw-login__subtitle{font-size:13px;color:rgba(212,175,110,.5);font-weight:500;margin-bottom:36px}

.dw-login__form{text-align:left}
.dw-login__label{display:block;font-size:12px;font-weight:700;color:rgba(212,175,110,.6);margin-bottom:8px;letter-spacing:.02em}
.dw-login__input{width:100%;padding:14px 16px;border-radius:14px;border:1.5px solid rgba(212,175,110,.15);background:rgba(255,255,255,.04);font-size:16px;color:var(--ivory);outline:none;font-family:var(--fb);transition:border-color .2s}
.dw-login__input::placeholder{color:rgba(212,175,110,.25)}
.dw-login__input:focus{border-color:rgba(212,175,110,.4)}
.dw-login__input--code{text-align:center;font-size:24px;letter-spacing:.3em;font-weight:700}
.dw-login__hint{font-size:11.5px;color:rgba(245,239,230,.3);margin-top:8px;margin-bottom:16px}
.dw-login__error{font-size:12px;color:#ef4444;margin-bottom:12px;padding:8px 12px;border-radius:10px;background:rgba(239,68,68,.08);border:1px solid rgba(239,68,68,.15)}

.dw-login__btn{width:100%;padding:15px 0;border-radius:14px;border:none;background:linear-gradient(135deg,var(--gold),var(--gold-dk));color:var(--dark);font-size:15px;font-weight:700;cursor:pointer;font-family:var(--fb);box-shadow:0 4px 16px rgba(212,175,110,.25);letter-spacing:.03em;transition:all .2s}
.dw-login__btn--loading{opacity:.6;cursor:not-allowed}
.dw-login__back{display:block;width:100%;margin-top:12px;padding:10px 0;background:none;border:none;color:rgba(212,175,110,.4);font-size:12.5px;font-weight:600;cursor:pointer;font-family:var(--fb)}

.dw-login__footer{position:absolute;bottom:24px;font-size:10px;color:rgba(212,175,110,.15);letter-spacing:.04em}
      `}</style>
    </div>
  );
}
